import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { fetchFallbackSpots, buildFallbackRecommendations } from "../lib/fallback-spots";
import { activityToSpot, fetchIslandActivities, getIslandSpotsSync } from "../lib/island-activities-api";
import { isProUser } from "../lib/patron";
import { applyTierToRecommendations, applyTierToSpots } from "../lib/tier-spots";
import { formatSpotLocation } from "../lib/format-spot-location";
import { getSiteProfile } from "../lib/site-profile";
import { CARIBBEAN_ISLANDS } from "../lib/caribbeanIslands";
import { useScrolledNav } from "../hooks/useScrolledNav";
import MeetupMap from "../components/MeetupMap";
import NaviGeneralChat from "../components/NaviGeneralChat";
import QuickOnboard from "../components/business/QuickOnboard";
import PartnerRevenueCalculator from "../components/business/PartnerRevenueCalculator";
import NaviProfitStreamsCard from "../components/business/NaviProfitStreamsCard";
import TrendingCarousel from "../components/TrendingCarousel";
import BrandImage from "../components/BrandImage";
import { Search, MapPin, Star, Sun, Cloud, CloudRain, Palmtree, UtensilsCrossed, Music, Waves, Sparkles, Crown, Menu, X, Lock, Calendar, Clock, Users, ArrowRight, Home, Compass, Gem, ChevronDown, BriefcaseBusiness, MessageCircleMore } from "lucide-react";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80",
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&q=80",
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
];

function LandingPage() {
  const { user } = useAuth();
  const siteProfile = getSiteProfile();
  const navigate = useNavigate();
  const explorerSectionRef = useRef(null);
  const partnerSectionRef = useRef(null);
  const chatSectionRef = useRef(null);
  const [search, setSearch] = useState("");
  const [weather, setWeather] = useState(null);
  const [featuredSpots, setFeaturedSpots] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("barbados");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [audienceMode, setAudienceMode] = useState("explorer");
  const [mapSpotContext, setMapSpotContext] = useState(null);
  const [, setIsLoading] = useState(true);

  const patronPro = isProUser(user);

  useEffect(() => {
    const instant = getIslandSpotsSync({ country: selectedCountry, isPro: patronPro });
    setFeaturedSpots(instant.spots.slice(0, 12));
    setRecommendations(
      applyTierToRecommendations(buildFallbackRecommendations(instant.spots, selectedCountry), {
        isPro: patronPro,
      })
    );

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const weatherRes = await axios.get(`${API}/weather/${selectedCountry}`);
        const spotsRes = await axios.get(`${API}/spots`, {
          params: { country: selectedCountry, limit: patronPro ? 50 : 12 },
          withCredentials: true,
        });
        const recsRes = await axios.get(`${API}/spots/recommendations/now`, {
          params: { country: selectedCountry },
          withCredentials: true,
        });
        const expRes = await axios.get(`${API}/experiences`, { params: { country: selectedCountry, limit: 4 } });

        setWeather(weatherRes.data);
        setExperiences(expRes.data.experiences || []);

        if (spotsRes.data?.limit_reached) {
          setShowUpgradeModal(true);
          const preview = applyTierToSpots(instant.spots, { isPro: false, limitReached: true }).slice(0, 12);
          setFeaturedSpots(preview);
          return;
        }

        const liveSpots = Array.isArray(spotsRes.data?.spots) ? spotsRes.data.spots : [];
        if (liveSpots.length === 0) {
          const islandData = await fetchIslandActivities({
            country: selectedCountry,
            isPro: patronPro,
          });
          const catalogSpots = (islandData.activities || []).map(activityToSpot);
          const merged =
            catalogSpots.length > 0
              ? catalogSpots
              : await fetchFallbackSpots({ country: selectedCountry, limit: 12, isPro: patronPro });
          const tiered = applyTierToSpots(merged, { isPro: patronPro });
          setFeaturedSpots(tiered.slice(0, 12));
          setRecommendations(
            applyTierToRecommendations(buildFallbackRecommendations(tiered, selectedCountry), {
              isPro: patronPro,
            })
          );
        } else {
          const tiered = patronPro ? liveSpots : applyTierToSpots(liveSpots, { isPro: false });
          setFeaturedSpots(tiered.slice(0, 12));
          const liveRecs = recsRes?.data?.spots?.length
            ? applyTierToRecommendations(recsRes.data, { isPro: patronPro })
            : applyTierToRecommendations(buildFallbackRecommendations(tiered, selectedCountry), {
                isPro: patronPro,
              });
          setRecommendations(liveRecs);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        const fallbackSpots = await fetchFallbackSpots({
          country: selectedCountry,
          limit: 12,
          isPro: patronPro,
        });
        setFeaturedSpots(fallbackSpots);
        setRecommendations(
          applyTierToRecommendations(buildFallbackRecommendations(fallbackSpots, selectedCountry), {
            isPro: patronPro,
          })
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedCountry, patronPro, user]);

  useEffect(() => {
    const interval = setInterval(() => { setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length); }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const syncHashNavigation = () => {
      const hash = window.location.hash;
      if (hash === "#partner-portal") {
        setAudienceMode("partner");
        requestAnimationFrame(() => {
          partnerSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else if (hash === "#map-friend-chat") {
        setAudienceMode("explorer");
        requestAnimationFrame(() => {
          chatSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    };

    syncHashNavigation();
    window.addEventListener("hashchange", syncHashNavigation);
    return () => window.removeEventListener("hashchange", syncHashNavigation);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/discover?search=${encodeURIComponent(search)}&country=${selectedCountry}`); }
  };

  const countries = CARIBBEAN_ISLANDS;
  const navScrolled = useScrolledNav(72);

  const categories = [
    { icon: Waves, label: "Beaches", value: "beach", color: "from-cyan-500 to-blue-600" },
    { icon: UtensilsCrossed, label: "Dining", value: "restaurant", color: "from-orange-500 to-red-600" },
    { icon: Music, label: "Nightlife", value: "nightclub", color: "from-purple-500 to-pink-600" },
    { icon: Palmtree, label: "Adventures", value: "adventure", color: "from-green-500 to-emerald-600" },
    { icon: Home, label: "Stays", value: "hotel", color: "from-amber-500 to-orange-600" },
    { icon: Gem, label: "Concierge", value: "concierge", color: "from-yellow-400 to-amber-500" },
  ];

  const isPro = patronPro;
  const isBusinessUser = user?.role === "business" || user?.role === "admin";

  const mansionManagedBusiness = useMemo(
    () =>
      featuredSpots.find((spot) => spot.category === "adventure" || spot.category === "restaurant") || {
        name: "Ocean Crest Surf School",
        location: "Bathsheba, Barbados",
        image_url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80",
      },
    [featuredSpots]
  );

  const jumpToAudience = (mode) => {
    setAudienceMode(mode);
    const target = mode === "partner" ? partnerSectionRef.current : explorerSectionRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getWeatherIcon = () => {
    if (!weather) return <Sun className="w-5 h-5 text-amber-400" />;
    const desc = (weather.description || "").toLowerCase();
    if (desc.includes("rain")) return <CloudRain className="w-5 h-5 text-blue-400" />;
    if (desc.includes("cloud")) return <Cloud className="w-5 h-5 text-gray-400" />;
    return <Sun className="w-5 h-5 text-amber-400" />;
  };

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white">
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="relative bg-white rounded-[2rem] p-8 md:p-12 max-w-lg w-full text-center shadow-2xl">
            <button onClick={() => setShowUpgradeModal(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg"><Crown className="w-10 h-10 text-white" /></div>
            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-3">Unlock Premium Access</h3>
            <p className="text-gray-600 mb-8 text-lg">Get unlimited access to hidden gems, exclusive bookings, and personalized concierge services.</p>
            <Link to="/premium"><Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full py-6 text-lg font-semibold shadow-lg"><Crown className="w-5 h-5 mr-2" />Upgrade to PRO</Button></Link>
            <p className="mt-4 text-sm text-gray-500">Starting at $9.99/month</p>
          </div>
        </div>
      )}

      {/* Navigation — below the 35px ticker */}
      <nav
        className={`fixed left-0 right-0 z-[5000] transition-all duration-300 top-[var(--mansion-ticker-height)] ${
          navScrolled ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100/80" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div
            className={`rounded-full mt-3 md:mt-4 px-4 md:px-6 py-3 flex items-center justify-between transition-all duration-300 ${
              navScrolled
                ? "glass-light"
                : "bg-white/20 backdrop-blur-md border border-white/35 shadow-lg"
            }`}
          >
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center"><Waves className="w-5 h-5 text-white" /></div>
              <span
                className={`font-serif font-bold text-lg hidden sm:block ${
                  navScrolled
                    ? "bg-gradient-to-r from-cyan-600 to-orange-500 bg-clip-text text-transparent"
                    : "text-white drop-shadow-sm"
                }`}
              >
                {siteProfile.brandName}
              </span>
            </Link>
            {weather && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-gray-100 text-sm shrink-0">
                {getWeatherIcon()}
                <span className="font-semibold">{Math.round(weather.temperature || 28)}°C</span>
                <span className="text-gray-500 hidden xl:inline">{weather.description || "Sunny"}</span>
              </div>
            )}
            <div className="hidden lg:flex items-center gap-0.5 shrink-0">
              <Link to="/discover" className={`px-4 py-2 rounded-full font-medium ${navScrolled ? "hover:bg-gray-100 text-gray-700" : "hover:bg-white/25 text-white"}`}>Discover</Link>
              <Link to="/products" className={`px-4 py-2 rounded-full font-medium ${navScrolled ? "hover:bg-gray-100 text-gray-700" : "hover:bg-white/25 text-white"}`}>Import Catalog</Link>
              <Link to="/experiences" className={`px-4 py-2 rounded-full font-medium ${navScrolled ? "hover:bg-gray-100 text-gray-700" : "hover:bg-white/25 text-white"}`}>Experiences</Link>
              <Link to="/assistant" className={`px-4 py-2 rounded-full font-medium ${navScrolled ? "hover:bg-gray-100 text-gray-700" : "hover:bg-white/25 text-white"}`}>Assistant</Link>
              {user ? (
                <Link to="/dashboard" className="ml-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-medium hover:shadow-lg">Dashboard</Link>
              ) : (
                <Link to="/auth" className="ml-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-medium hover:shadow-lg">Get Started</Link>
              )}
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-full hover:bg-gray-100">{mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 p-4">
            <div className="glass-light rounded-2xl p-4 flex flex-col gap-2">
              <Link to="/discover" className="px-4 py-3 rounded-xl hover:bg-gray-100 font-medium">Discover</Link>
              <Link to="/experiences" className="px-4 py-3 rounded-xl hover:bg-gray-100 font-medium">Experiences</Link>
              <Link to="/assistant" className="px-4 py-3 rounded-xl hover:bg-gray-100 font-medium">Assistant</Link>
              {user ? <Link to="/dashboard" className="px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-medium text-center">Dashboard</Link> : <Link to="/auth" className="px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-medium text-center">Get Started</Link>}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section — top-aligned so island pills + search are not clipped */}
      <section className="relative min-h-hero-viewport overflow-x-hidden">
        {HERO_IMAGES.map((img, idx) => (
          <div key={idx} className={`absolute inset-0 transition-opacity duration-1000 ${idx === heroIndex ? "opacity-100" : "opacity-0"}`}>
            <img src={img} alt="Caribbean" className="w-full h-full object-cover object-[center_35%]" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
        <div className="absolute inset-0 max-w-4xl mx-auto bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.45)_0%,transparent_70%)] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-[calc(var(--mansion-nav-height)+0.5rem)] pb-12">
          <div className="flex justify-start md:justify-center gap-2 mb-5 overflow-x-auto scrollbar-hide px-1 pb-1 max-w-full">
            {countries.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => setSelectedCountry(c.code)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all border ${
                  selectedCountry === c.code
                    ? "bg-white text-gray-900 shadow-lg border-white"
                    : "bg-black/35 text-white hover:bg-black/45 backdrop-blur-sm border-white/25"
                }`}
              >
                <span className="mr-1">{c.flag}</span>
                <span className="hidden sm:inline">{c.name}</span>
                <span className="sm:hidden">{c.name.split(" ")[0]}</span>
              </button>
            ))}
          </div>
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-1 rounded-full bg-black/35 backdrop-blur-md border border-white/20 px-1 py-1 mb-5">
              <button
                type="button"
                onClick={() => setAudienceMode("explorer")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  audienceMode === "explorer" ? "bg-white text-slate-900 shadow" : "text-white/85"
                }`}
              >
                Travelers
              </button>
              <button
                type="button"
                onClick={() => setAudienceMode("partner")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  audienceMode === "partner" ? "bg-white text-slate-900 shadow" : "text-white/85"
                }`}
              >
                Partners
              </button>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-4 leading-[1.1] drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]">
              {audienceMode === "explorer" ? siteProfile.heroTitlePrefix : "List Your"}
              <span className="block mt-1 bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 bg-clip-text text-transparent drop-shadow-none">
                {audienceMode === "explorer" ? siteProfile.heroTitleAccent : "Experience"}
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/95 mb-6 max-w-2xl mx-auto drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
              {audienceMode === "explorer"
                ? "A premium concierge for travelers who want authentic and easy: verified pins, live local tips, and total-price transparency."
                : "A growth partner for businesses that want to be visible and profitable: clearer payouts, faster onboarding, and Mansion-managed merchandising."}
            </p>

            {audienceMode === "explorer" ? (
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-orange-500 rounded-full blur-lg opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="relative glass-light rounded-full p-2 flex items-center gap-2">
                    <div className="flex items-center px-4"><Search className="w-5 h-5 text-gray-400" /></div>
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search beaches, restaurants, experiences..." className="flex-1 border-0 bg-transparent text-gray-900 placeholder:text-gray-500 focus-visible:ring-0 text-lg" />
                    <Button type="submit" className="rounded-full px-8 py-6 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold shadow-lg">Explore</Button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="max-w-2xl mx-auto rounded-[1.75rem] bg-white/12 backdrop-blur-md border border-white/15 p-5 text-left">
                <p className="text-sm uppercase tracking-[0.18em] text-amber-200 font-semibold mb-2">
                  Partner Promise
                </p>
                <p className="text-white/90 text-sm">
                  Travelers see authentic and easy. You see your payout, VAT/GCT collected, and Mansion fee before you ever publish a listing.
                </p>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-6">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <Link key={cat.value} to={`/discover?category=${cat.value}&country=${selectedCountry}`} className="group relative">
                    <div className={`absolute inset-0 bg-gradient-to-r ${cat.color} rounded-full blur-md opacity-0 group-hover:opacity-50 transition-opacity`} />
                    <div className="relative flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-sm"><Icon className="w-4 h-4" /><span className="font-medium text-gray-800">{cat.label}</span></div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="mt-8 max-w-sm mx-auto hidden md:block">
            <div className="rounded-[2rem] overflow-hidden border border-white/20 bg-slate-950/55 backdrop-blur-md shadow-2xl">
              <img
                src={mansionManagedBusiness.image_url}
                alt={mansionManagedBusiness.name}
                className="w-full h-48 object-cover"
              />
              <div className="p-5 text-left">
                <Badge className="bg-white/10 text-white border-0 mb-3">Managed by Mansion Engine</Badge>
                <h3 className="font-serif font-bold text-white text-xl">{mansionManagedBusiness.name}</h3>
                <p className="text-white/70 text-sm mt-1">{formatSpotLocation(mansionManagedBusiness)}</p>
                <p className="text-white/80 text-sm mt-3">
                  Premium for travelers. Profitable for businesses. Built to feel like a members club and a revenue tool at the same time.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"><ChevronDown className="w-8 h-8 text-white/70" /></div>
      </section>

      <section className="sticky z-40 bg-white/90 backdrop-blur-md border-y border-slate-100 top-[calc(var(--mansion-ticker-height)+var(--mansion-nav-height))]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => jumpToAudience("explorer")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                audienceMode === "explorer" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              Explorer Map
            </button>
            <button
              type="button"
              onClick={() => jumpToAudience("partner")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                audienceMode === "partner" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              Partner Mode
            </button>
          </div>
        </div>
      </section>

      <section id="explorer-map" ref={explorerSectionRef} className="scroll-mt-32 py-20 px-4 md:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-cyan-600 font-semibold mb-2">
                Explorer Trust Stack
              </p>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900">
                Verified pins, local guidance, and transparent pricing
              </h2>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-0 px-4 py-2">
              ECOLOGY BALANCED
            </Badge>
          </div>

          <div className="grid xl:grid-cols-[1.25fr_0.85fr] gap-6">
            <div className="space-y-4">
              <MeetupMap
                country={selectedCountry}
                onCountryChange={setSelectedCountry}
                isPro={isPro}
                businessView={isBusinessUser}
                onPinSelect={setMapSpotContext}
                onOpenChat={(pin) => {
                  setMapSpotContext(pin);
                  window.location.hash = "map-friend-chat";
                  chatSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
              <div className="md:hidden flex gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    if (!mapSpotContext) {
                      explorerSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                      return;
                    }
                    window.location.hash = "map-friend-chat";
                    chatSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="flex-1 rounded-full"
                >
                  <MessageCircleMore className="w-4 h-4 mr-2" />
                  {mapSpotContext ? "Chat about pin" : "Pick a pin first"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => jumpToAudience("partner")}
                  className="flex-1 rounded-full"
                >
                  <BriefcaseBusiness className="w-4 h-4 mr-2" />
                  Partner Mode
                </Button>
              </div>
            </div>
            <div id="map-friend-chat" ref={chatSectionRef}>
              <NaviGeneralChat
                spotContext={mapSpotContext}
                onClearSpotContext={() => setMapSpotContext(null)}
              />
            </div>
          </div>
        </div>
      </section>

      <section id="partner-portal" ref={partnerSectionRef} className="py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-[0.18em] text-orange-300 font-semibold mb-2">
              Partner Welcome
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">
              Premium visibility, clean economics, faster onboarding
            </h2>
          </div>

          <div className="mb-6">
            <NaviProfitStreamsCard />
          </div>

          <div className="grid xl:grid-cols-2 gap-6">
            <PartnerRevenueCalculator />
            <QuickOnboard />
          </div>
        </div>
      </section>

      {/* AI Recommendations */}
      {recommendations && recommendations.spots && recommendations.spots.length > 0 && (
        <section className="py-20 px-4 md:px-8 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2"><div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500"><Sparkles className="w-5 h-5 text-white" /></div><span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">AI Powered</span></div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2">{recommendations.title || "Recommended for You"}</h2>
            <p className="text-gray-600 mb-10">{recommendations.context || "Based on current time and weather"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recommendations.spots.map((spot) => <SpotCard key={spot.spot_id} spot={spot} isPro={isPro} />)}
            </div>
          </div>
        </section>
      )}

      {/* Trending marketplace pulse */}
      <section className="py-12 px-4 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <TrendingCarousel siteRole={siteProfile.id} title="Trending Across the Mansion Network" />
        </div>
      </section>

      {/* Featured Spots */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-2"><div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500"><Compass className="w-5 h-5 text-white" /></div><span className="text-sm font-semibold text-cyan-600 uppercase tracking-wider">Explore</span></div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold">Discover {countries.find(c => c.code === selectedCountry)?.name}</h2>
            </div>
            <Link to={`/discover?country=${selectedCountry}`} className="mt-4 md:mt-0 inline-flex items-center gap-2 text-cyan-600 font-semibold hover:gap-3 transition-all">View All <ArrowRight className="w-5 h-5" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featuredSpots.slice(0, 8).map((spot) => <SpotCard key={spot.spot_id} spot={spot} isPro={isPro} />)}
          </div>
        </div>
      </section>

      {/* Experiences */}
      {experiences.length > 0 && (
        <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
              <div>
                <div className="flex items-center gap-3 mb-2"><div className="p-2 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500"><Calendar className="w-5 h-5 text-white" /></div><span className="text-sm font-semibold text-orange-400 uppercase tracking-wider">Book Now</span></div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold">Unforgettable Experiences</h2>
              </div>
              <Link to="/experiences" className="mt-4 md:mt-0 inline-flex items-center gap-2 text-orange-400 font-semibold hover:gap-3 transition-all">View All <ArrowRight className="w-5 h-5" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {experiences.map((exp) => <ExperienceCard key={exp.experience_id} experience={exp} />)}
            </div>
          </div>
        </section>
      )}

      {/* Premium CTA */}
      {!isPro && (
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 p-1">
              <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 rounded-[1.8rem] p-8 md:p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"><Crown className="w-10 h-10 text-white" /></div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">Unlock the Full Experience</h2>
                <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">Get unlimited access to hidden gems, exclusive bookings, premium concierge, and personalized recommendations.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/premium"><Button className="w-full sm:w-auto px-8 py-6 rounded-full bg-white text-orange-600 font-bold text-lg hover:bg-gray-50 shadow-xl">Start Free Trial</Button></Link>
                  <Link to="/premium"><Button variant="outline" className="w-full sm:w-auto px-8 py-6 rounded-full border-2 border-white/50 text-white font-bold text-lg hover:bg-white/10">View Plans</Button></Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-16 px-4 md:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center"><Waves className="w-5 h-5 text-white" /></div><span className="font-serif font-bold text-lg">{siteProfile.brandName}</span></div>
              <p className="text-gray-400 text-sm">The Digital Pulse of Barbados</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Discover</h4>
              <div className="flex flex-col gap-2 text-gray-400 text-sm">
                <Link to="/discover?category=beach" className="hover:text-white">Beaches</Link>
                <Link to="/discover?category=restaurant" className="hover:text-white">Dining</Link>
                <Link to="/discover?category=nightclub" className="hover:text-white">Nightlife</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <div className="flex flex-col gap-2 text-gray-400 text-sm">
                <Link to="/experiences" className="hover:text-white">Experiences</Link>
                <Link to="/assistant" className="hover:text-white">Assistant</Link>
                <Link to="/premium" className="hover:text-white">Premium</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <div className="flex flex-col gap-2 text-gray-400 text-sm">
                <Link to="/about" className="hover:text-white">About Us</Link>
                <Link to="/contact" className="hover:text-white">Contact</Link>
                <a href="https://digitalhustlerscaribbean.com/privacy" className="hover:text-white" rel="noopener noreferrer">Privacy</a>
                <a href="https://digitalhustlerscaribbean.com/terms" className="hover:text-white" rel="noopener noreferrer">Terms</a>
                <a href="https://digitalhustlerscaribbean.com/cookies" className="hover:text-white" rel="noopener noreferrer">Cookies</a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2026 {siteProfile.brandName}. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-3 max-w-full">{countries.map(c => <button key={c.code} onClick={() => setSelectedCountry(c.code)} className={`text-2xl transition-opacity ${selectedCountry === c.code ? "opacity-100" : "opacity-50 hover:opacity-75"}`}>{c.flag}</button>)}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SpotCard({ spot, isPro }) {
  const isLocked = spot.locked && !isPro;
  const locationText = formatSpotLocation(spot);
  
  return (
    <Link to={`/spot/${spot.spot_id}`} className="group relative block rounded-[1.5rem] overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 h-72">
      <div className="absolute inset-0">
        <BrandImage
          src={spot.image_url || (spot.images && spot.images[0])}
          alt={spot.name}
          className="h-full w-full transition-transform duration-700 group-hover:scale-110"
          verified={Boolean(spot.is_verified_image)}
          showVerifiedBadge={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      </div>
      {isLocked && <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><div className="text-center text-white p-4"><Lock className="w-8 h-8 mx-auto mb-2" /><p className="font-semibold">PRO Content</p></div></div>}
      <div className="absolute top-4 left-4 flex gap-2">{spot.is_featured && <Badge className="bg-amber-500 text-white border-0">Featured</Badge>}{spot.is_premium_content && <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0"><Crown className="w-3 h-3 mr-1" /> Premium</Badge>}</div>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-2"><MapPin className="w-4 h-4" /><span>{locationText}</span>{spot.rating && <><span className="mx-1">•</span><Star className="w-4 h-4 text-amber-400 fill-amber-400" /><span>{spot.rating}</span></>}</div>
        <h3 className="font-serif font-bold text-white text-lg">{spot.name}</h3>
      </div>
      <div className="absolute bottom-5 right-5 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0"><ArrowRight className="w-5 h-5 text-white" /></div>
    </Link>
  );
}

function ExperienceCard({ experience }) {
  return (
    <Link to={`/experience/${experience.experience_id}`} className="group relative block rounded-[1.5rem] overflow-hidden bg-white/10 backdrop-blur-sm border border-white/10 hover:border-white/30 transition-all duration-500 hover:-translate-y-1">
      <div className="relative h-48 overflow-hidden">
        <BrandImage src={experience.image_url} alt={experience.name} className="h-full w-full transition-transform duration-700 group-hover:scale-110" showVerifiedBadge={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-4 left-4">
          <div className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold">${experience.price}</div>
        </div>
      </div>
      <div className="p-5"><h3 className="font-serif font-bold text-white text-lg mb-2 line-clamp-1">{experience.name}</h3><div className="flex items-center gap-4 text-white/70 text-sm"><div className="flex items-center gap-1"><Clock className="w-4 h-4" /><span>{experience.duration || "3h"}</span></div><div className="flex items-center gap-1"><Users className="w-4 h-4" /><span>Up to {experience.max_participants || 10}</span></div></div></div>
    </Link>
  );
}

export default LandingPage;
